import { useCallback, useMemo, useState } from "react";
import {
	DeviceCategory,
	isDeviceLocated,
	LocatedVelavuDevice,
	VelavuAPI,
	VelavuDevice,
	VelavuSite,
} from "velavu-js-api";
import booleanIntersects from "@turf/boolean-intersects";
import { point } from "@turf/helpers";
import distance from "@turf/distance";
import { useGlobalEmitter } from "../emitter/emitter-context";
import { SiteDevicesCacheType } from "../components/dashboard/dropdown/site-detail/site-devices-cache";
import { getFloorTarget } from "../helper/floor-helper";

export enum AnchorPositioningFeedback {
	Far,
	OK,
	Close,
	NoSite,
}

export interface PinAnchorState {
	selection: VelavuDevice | null;
	location: [number, number] | null;
	setLocation: (location: [number, number] | null) => void;
	positioningFeedback: AnchorPositioningFeedback | undefined;
	otherAnchors: LocatedVelavuDevice[];

	loading: boolean;

	setSelection: (selection: VelavuDevice | null) => void;
	unpin: (device: VelavuDevice) => void;
	submit: () => void;
}

//If the anchor is closer than this value (meters) to another anchor, it is deemed too close
const THRESHOLD_RANGE_CLOSE = 5;
//If the anchor is further than this value (meters) to another anchor, it is deemed too far
const THRESHOLD_RANGE_FAR = 15;

/**
 * Maps a distance between 2 anchors to a {@link AnchorPositioningFeedback}
 * @param distance The distance in meters between 2 anchors
 */
function mapDistance(distance: number): AnchorPositioningFeedback {
	if (distance <= THRESHOLD_RANGE_CLOSE)
		return AnchorPositioningFeedback.Close;
	else if (distance >= THRESHOLD_RANGE_FAR)
		return AnchorPositioningFeedback.Far;
	else return AnchorPositioningFeedback.OK;
}

const EMPTY_DEVICE_ARRAY: readonly VelavuDevice[] = [];

/**
 * Manages the state for pinning the location of an anchor device
 * @param sites The available sites
 * @param siteDevicesCache A cache used to look up the devices of the active site
 * @param activeSiteID The ID of the site linked to the floor picker on the map, or undefined if no site is active
 * @param floorOverride A mapping of site IDs to their user-selected floor ID
 * @param updateCallback A callback invoked with the new device when the device is updated
 */
export function usePinAnchorState(
	sites: readonly VelavuSite[] | undefined,
	siteDevicesCache: SiteDevicesCacheType,
	activeSiteID: string | undefined,
	floorOverride: Readonly<{ [siteID: string]: string | undefined }>,
	updateCallback?: (device: VelavuDevice) => void,
): PinAnchorState {
	const emitter = useGlobalEmitter();

	const [selection, setSelection] = useState<VelavuDevice | null>(null);
	const [location, setLocation] = useState<[number, number] | null>(null);
	const [loading, setLoading] = useState(false);

	const setSelectionWrapper = useCallback(
		(newSelection: VelavuDevice | null) => {
			setSelection(newSelection);
			setLocation(null);
		},
		[setSelection, setLocation],
	);

	//Resolve the site and floor linked to the floor picker on the map
	const activeSite = useMemo((): VelavuSite | undefined => {
		if (activeSiteID === undefined) {
			return undefined;
		}

		return sites?.find((site) => site.id === activeSiteID);
	}, [sites, activeSiteID]);
	const activeFloorID = useMemo((): string | undefined => {
		if (activeSite === undefined) {
			return undefined;
		}

		return getFloorTarget(activeSite.floors, floorOverride[activeSite.id])
			?.id;
	}, [activeSite, floorOverride]);

	//Only look up the devices of the active site while pinning
	const siteDevices =
		selection === null || activeSiteID === undefined
			? EMPTY_DEVICE_ARRAY
			: (siteDevicesCache.get(activeSiteID) ?? EMPTY_DEVICE_ARRAY);

	const otherAnchors = useMemo((): LocatedVelavuDevice[] => {
		if (selection === null || activeFloorID === undefined) {
			return [];
		}

		return (
			siteDevices
				//Two filters to make TypeScript happy
				.filter(isDeviceLocated)
				.filter((device) => {
					return (
						device.id !== selection.id &&
						device.category === DeviceCategory.Anchor &&
						"floor_id" in device.location &&
						device.location.floor_id === activeFloorID
					);
				})
		);
	}, [selection, activeFloorID, siteDevices]);

	const positioningFeedback = useMemo(():
		AnchorPositioningFeedback | undefined => {
		//Make sure we have a selected anchor and location
		if (selection === null || location === null) {
			return undefined;
		}

		//Make sure the location falls within the active site
		if (
			activeSite === undefined ||
			activeSite.boundary === undefined ||
			!booleanIntersects(activeSite.boundary, point(location))
		) {
			return AnchorPositioningFeedback.NoSite;
		}

		//Find the closest other anchor
		const otherAnchorDistances = otherAnchors.map((otherAnchor) => {
			return distance(location, otherAnchor.location.coordinates, {
				units: "meters",
			});
		});
		const closestDistance = Math.min(...otherAnchorDistances);
		return mapDistance(closestDistance);
	}, [location, otherAnchors, selection, activeSite]);

	const submit = useCallback(async () => {
		if (loading || selection === null || location === null) {
			return;
		}

		setLoading(true);
		let updatedDevice: VelavuDevice;
		try {
			updatedDevice = await VelavuAPI.devices.modifySpecificDevice(
				selection.id,
				{
					location: {
						floor_id: activeFloorID,
						coordinates: location,
						locked: true,
					},
				},
			);
		} catch (error) {
			console.warn("Failed to update device location", error);
			return;
		} finally {
			setLoading(false);
		}

		setSelectionWrapper(null);
		emitter.emit("device", {
			id: updatedDevice.id,
			data: updatedDevice,
		});
		updateCallback?.(updatedDevice);
	}, [
		loading,
		selection,
		location,
		activeFloorID,
		setSelectionWrapper,
		emitter,
		updateCallback,
	]);

	const unpin = useCallback(
		async (device: VelavuDevice) => {
			if (loading) {
				return;
			}

			setLoading(true);
			let updatedDevice: VelavuDevice;
			try {
				updatedDevice = await VelavuAPI.devices
					.removeSpecificDeviceLocation(device.id)
					.then(() => ({
						...device,
						location: undefined,
					}));
			} catch (error) {
				console.warn("Failed to unpin device", error);
				return;
			} finally {
				setLoading(false);
			}

			emitter.emit("device", {
				id: updatedDevice.id,
				data: updatedDevice,
			});
			updateCallback?.(updatedDevice);
		},
		[loading, emitter, updateCallback],
	);

	return useMemo(
		(): PinAnchorState => ({
			selection,
			location,
			setLocation,
			positioningFeedback,
			otherAnchors,
			loading,
			setSelection: setSelectionWrapper,
			unpin,
			submit,
		}),
		[
			selection,
			location,
			setLocation,
			positioningFeedback,
			otherAnchors,
			loading,
			setSelectionWrapper,
			unpin,
			submit,
		],
	);
}
